// PageHeader. Unified header pattern shared by all non-Sale views.
// Back button + optional logo + title + optional operator name + actions.
// See DESIGN.md §9.14 and §9.15.

import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";

type Props = {
  title: string;
  showLogo?: boolean;
  showOperator?: boolean;
  operatorName?: string;
  onBack?: () => void;
  backHref?: string;
  backLabel?: string;
  actions?: ReactNode;
  variant?: "compact" | "full";
  style?: CSSProperties;
};

export function PageHeader({
  title,
  showLogo = false,
  showOperator = false,
  operatorName,
  onBack,
  backHref,
  backLabel = "Volver",
  actions,
  variant = "compact",
  style,
}: Props) {
  const hasBack = !!onBack || !!backHref;
  const minHeight = variant === "full" ? 64 : 56;

  const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-2)",
    padding: "var(--space-3) var(--space-4)",
    borderBottom: "1px solid var(--border-subtle)",
    background: "var(--surface-base)",
    minHeight,
    flexWrap: "wrap",
    ...style,
  };

  const leftStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    minWidth: 0,
    flex: "1 1 auto",
  };

  const titleStackStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    gap: 2,
  };

  const titleStyle: CSSProperties = {
    fontSize: "var(--font-size-16)",
    fontWeight: 600,
    color: "var(--text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const operatorStyle: CSSProperties = {
    fontSize: "var(--font-size-13)",
    color: "var(--text-secondary)",
  };

  const actionsStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  };

  const backStyle: CSSProperties = {
    color: "var(--text-secondary)",
    textDecoration: "none",
    fontSize: "var(--font-size-14)",
    fontWeight: 500,
    padding: "var(--space-1) var(--space-2)",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
  };

  const renderBack = () => {
    if (!hasBack) return null;
    if (backHref != null) {
      return (
        <Link to={backHref} aria-label={backLabel} style={backStyle}>
          ← {backLabel}
        </Link>
      );
    }
    return (
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        style={{ ...backStyle, background: "transparent", border: "none" }}
      >
        ← {backLabel}
      </button>
    );
  };

  return (
    <header style={headerStyle}>
      <div style={leftStyle}>
        {renderBack()}
        {showLogo && <Logo height={variant === "full" ? 22 : 18} />}
        <div style={titleStackStyle}>
          <span style={titleStyle}>{title}</span>
          {showOperator && operatorName && (
            <span style={operatorStyle}>{operatorName}</span>
          )}
        </div>
      </div>
      {actions && <div style={actionsStyle}>{actions}</div>}
    </header>
  );
}